import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, MapPin, Calendar, Users, Eye, Search, ChevronDown, ChevronRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import AllotModal from "../components/AllotModal";

export default function Households() {
  const { user } = useAuth();
  const isFaculty = user?.role === "faculty" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all"); // all | mine | assigned
  const [collapsed, setCollapsed] = useState({});
  const [allotting, setAllotting] = useState(null); // household being allotted

  useEffect(() => {
    api.get("/households")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((h) => {
    if (scope === "mine" && h.created_by !== user.id) return false;
    if (scope === "assigned" && !(h.assigned_to || []).includes(user.id)) return false;
    if (q) {
      const s = q.toLowerCase();
      return (h.village || "").toLowerCase().includes(s)
        || (h.head_of_family || "").toLowerCase().includes(s)
        || (h.address || "").toLowerCase().includes(s)
        || (h.created_by_name || "").toLowerCase().includes(s)
        || ((h.assignees || []).some((a) => (a.name || "").toLowerCase().includes(s)));
    }
    return true;
  });

  // Group by village (Unknown for missing)
  const groups = useMemo(() => {
    const m = new Map();
    for (const h of filtered) {
      const v = h.village || "Unknown";
      if (!m.has(v)) m.set(v, []);
      m.get(v).push(h);
    }
    return Array.from(m.entries())
      .map(([village, list]) => ({
        village,
        list: list.sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || "")),
        patients: list.reduce((s, h) => s + (h.family_members || []).filter((m) => m.is_patient).length, 0),
        people: list.reduce((s, h) => s + (h.family_members || []).length, 0),
      }))
      .sort((a, b) => b.list.length - a.list.length);
  }, [filtered]);

  const toggle = (v) => setCollapsed((c) => ({ ...c, [v]: !c[v] }));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Field data</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Households</h1>
          <p className="text-brand-muted mt-1 text-sm">Browse any household to view history and add follow-up visits</p>
        </div>
        <Link
          to="/households/new"
          data-testid="add-household-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors"
        >
          <Plus className="h-4 w-4" /> New Visit
        </Link>
      </div>

      {/* Filter / search */}
      <div className="bg-white border border-brand-border rounded-lg p-3 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search village, family head, address or recorded-by…"
            className="w-full h-11 pl-9 pr-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none text-sm"
            data-testid="hh-search"
          />
        </div>
        <div className="inline-flex rounded-md border border-brand-border bg-brand-surfaceAlt p-1">
          {[
            { k: "all", l: "All" },
            { k: "mine", l: "Mine" },
            ...(user?.role === "student" ? [{ k: "assigned", l: "Assigned" }] : []),
          ].map((o) => (
            <button
              key={o.k}
              type="button"
              onClick={() => setScope(o.k)}
              data-testid={`scope-${o.k}`}
              className={`px-4 h-9 rounded text-sm font-medium ${scope === o.k ? "bg-white shadow text-brand-text" : "text-brand-muted"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-brand-muted">Loading…</div>
      ) : filtered.length === 0 ? (
        items.length === 0 ? <EmptyState /> : <div className="text-brand-muted text-sm">No households match your filters.</div>
      ) : (
        <div className="space-y-8" data-testid="village-groups">
          {groups.map(({ village, list, patients, people }) => {
            const isCollapsed = !!collapsed[village];
            return (
              <section key={village} data-testid={`village-group-${village}`}>
                <button
                  type="button"
                  onClick={() => toggle(village)}
                  className="w-full flex items-center justify-between gap-3 mb-4 group"
                  data-testid={`village-toggle-${village}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-md bg-brand-primary/10 text-brand-primary grid place-items-center">
                      {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Village</div>
                      <h2 className="font-heading text-xl font-semibold leading-tight">{village}</h2>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2.5 py-1 rounded-full bg-brand-surfaceAlt border border-brand-border text-brand-text font-medium">{list.length} household{list.length > 1 ? "s" : ""}</span>
                    <span className="hidden sm:inline px-2.5 py-1 rounded-full bg-brand-surfaceAlt border border-brand-border text-brand-text font-medium">{people} people</span>
                    {patients > 0 && <span className="px-2.5 py-1 rounded-full bg-brand-accent/15 text-[#9C5240] font-semibold">{patients} patient{patients > 1 ? "s" : ""}</span>}
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((h) => {
                      const mine = h.created_by === user?.id;
                      const assignees = h.assignees || [];
                      const assignedToMe = (h.assigned_to || []).includes(user?.id);
                      return (
                        <div key={h.id} className="bg-white border border-brand-border rounded-lg p-5 hover:border-brand-primary transition-colors group flex flex-col" data-testid={`household-card-${h.id}`}>
                          <Link to={`/households/${h.id}`} className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-heading text-lg font-semibold truncate">{h.head_of_family}</div>
                                <div className="text-xs text-brand-muted mt-0.5 inline-flex items-center gap-1">
                                  <Calendar className="h-3 w-3" /> {h.visit_date}
                                </div>
                              </div>
                              <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded shrink-0 ${
                                h.overall_hygiene === "good" || h.overall_hygiene === "excellent"
                                  ? "bg-[#4A7C59]/10 text-[#4A7C59]"
                                  : h.overall_hygiene === "poor"
                                  ? "bg-[#C75D4A]/10 text-[#C75D4A]"
                                  : "bg-[#E8B25C]/15 text-[#9a7536]"
                              }`}>
                                {h.overall_hygiene}
                              </span>
                            </div>
                            <div className="mt-4 space-y-1.5 text-sm text-brand-muted">
                              <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {h.family_members?.length || 0} members</div>
                              {h.address && <div className="flex items-center gap-2 truncate"><MapPin className="h-3.5 w-3.5" /> {h.address}</div>}
                            </div>

                            {/* Assignees row */}
                            {assignees.length > 0 && (
                              <div className="mt-3 flex items-center gap-2 text-xs">
                                <div className="flex -space-x-2">
                                  {assignees.slice(0, 4).map((a, i) => {
                                    const ini = (a.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
                                    return (
                                      <span key={a.id} title={a.name} className="h-6 w-6 rounded-full border-2 border-white bg-brand-accent/15 text-[#9C5240] text-[10px] font-bold grid place-items-center" style={{ zIndex: 10 - i }}>
                                        {ini}
                                      </span>
                                    );
                                  })}
                                </div>
                                <span className="text-brand-muted">
                                  {assignees.length === 1 ? "Allotted to 1 student" : `Allotted to ${assignees.length} students`}
                                </span>
                              </div>
                            )}
                          </Link>

                          <div className="mt-4 pt-4 border-t border-brand-border flex items-center justify-between gap-2 text-xs">
                            <span className={`inline-flex items-center gap-1 ${mine ? "text-brand-primary font-semibold" : assignedToMe ? "text-[#9C5240] font-semibold" : "text-brand-muted"}`}>
                              {mine ? "Yours" : assignedToMe ? "Assigned to you" : `by ${h.created_by_name}`}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {isFaculty && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setAllotting(h); }}
                                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt font-medium"
                                  data-testid={`allot-btn-${h.id}`}
                                >
                                  <UserPlus className="h-3.5 w-3.5" /> Allot
                                </button>
                              )}
                              <Link to={`/households/${h.id}`} className="text-brand-primary inline-flex items-center gap-1 font-medium group-hover:gap-1.5 transition-all">
                                <Eye className="h-3.5 w-3.5" /> View
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {allotting && (
        <AllotModal
          household={allotting}
          onClose={() => setAllotting(null)}
          onSaved={(updated) => setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)))}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border-2 border-dashed border-brand-border bg-white p-12 text-center">
      <div className="mx-auto h-14 w-14 rounded-full bg-brand-surfaceAlt grid place-items-center">
        <Users className="h-7 w-7 text-brand-primary" />
      </div>
      <h3 className="font-heading text-xl font-semibold mt-4">No household visits yet</h3>
      <p className="text-brand-muted mt-2 text-sm max-w-md mx-auto">
        Start by recording your first home visit — capture family details, hygiene observations and health assessment.
      </p>
      <Link
        to="/households/new"
        className="inline-flex mt-6 items-center gap-2 h-11 px-5 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors"
        data-testid="empty-add-household-btn"
      >
        <Plus className="h-4 w-4" /> Record First Visit
      </Link>
    </div>
  );
}
